import { Edit } from '@mui/icons-material'
import { Loading } from 'components'
import { UserProps } from 'context/user/user.types'
import { translate } from 'locales'
import React, { useEffect, useState } from 'react'
import { alertDanger, alertSuccess } from 'utils/toast'
import BankDataForm from './BankDataForm'
import { findBankAccountData, updateBankAccountData } from '../services'
import { IBankAccountData } from '../types'
import { getAccountTypeLabel, getInitials, getPixKeyTypeLabel } from '../utils'
import * as S from './styles'

interface BankDataProps {
  userData: UserProps
}

const EMPTY_BANK_ACCOUNT: IBankAccountData = {
  holderName: '',
  cpf: '',
  companyName: '',
  cnpj: '',
  bankCode: '',
  bankName: '',
  accountType: '',
  agency: '',
  accountNumber: '',
  pixKeyType: '',
  pixKey: '',
}

const BankData = ({ userData }: BankDataProps) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [account, setAccount] = useState<IBankAccountData | null>(null)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    findBankAccountData(setAccount, setLoading, setLoadError)
  }, [userData.id])

  const handleSave = async (updated: IBankAccountData) => {
    try {
      setSaving(true)
      const result = await updateBankAccountData(updated)
      setAccount(result)
      setIsEditing(false)
      alertSuccess(translate('financial.bankData.saveSuccess'))
    } catch (err: any) {
      alertDanger(err?.response?.data?.message || translate('errors.errorTry'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <S.Section>
      <S.Header>
        <div className="account-holder">
          <S.Avatar hasPhoto={Boolean(userData.profile.photo)}>
            {userData.profile.photo ? (
              <img src={userData.profile.photo} alt={userData.name} />
            ) : (
              getInitials(userData.name)
            )}
          </S.Avatar>

          <div className="account-holder-details">
            <span className="doctor-name">{userData.name}</span>

            <span className="account-subtitle">
              {translate('financial.bankData.accountSubtitle')}
            </span>
          </div>
        </div>

        {!isEditing && !loadError && (
          <S.EditButton
            type="button"
            onClick={() => setIsEditing(true)}
            disabled={loading}
          >
            <Edit />
            {translate('financial.bankData.editButton')}
          </S.EditButton>
        )}
      </S.Header>

      {loading && <Loading title={translate('financial.loading')} />}

      {!loading && isEditing && (
        <>
          <S.Divider />

          <BankDataForm
            account={account ?? EMPTY_BANK_ACCOUNT}
            saving={saving}
            onCancel={() => setIsEditing(false)}
            onSave={handleSave}
          />
        </>
      )}

      {!loading && account && !isEditing && (
        <>
          <S.Divider />

          <S.FieldsGrid>
            <S.Field>
              <span className="field-label">
                {translate('financial.bankData.accountHolder')}
              </span>
              <span className="field-value">{account.holderName}</span>
            </S.Field>

            <S.Field>
              <span className="field-label">
                {translate('financial.bankData.cpf')}
              </span>
              <span className="field-value">{account.cpf}</span>
            </S.Field>

            <S.Field>
              <span className="field-label">
                {translate('financial.bankData.companyName')}
              </span>
              <span className="field-value">{account.companyName}</span>
            </S.Field>

            <S.Field>
              <span className="field-label">
                {translate('financial.bankData.cnpj')}
              </span>
              <span className="field-value">{account.cnpj}</span>
            </S.Field>

            <S.Field>
              <span className="field-label">
                {translate('financial.bankData.bank')}
              </span>
              <span className="field-value">
                {account.bankCode} · {account.bankName}
              </span>
            </S.Field>

            <S.Field>
              <span className="field-label">
                {translate('financial.bankData.accountType')}
              </span>
              <span className="field-value">
                {getAccountTypeLabel(account.accountType)}
              </span>
            </S.Field>

            <S.Field>
              <span className="field-label">
                {translate('financial.bankData.agency')}
              </span>
              <span className="field-value">{account.agency}</span>
            </S.Field>

            <S.Field>
              <span className="field-label">
                {translate('financial.bankData.account')}
              </span>
              <span className="field-value">{account.accountNumber}</span>
            </S.Field>
          </S.FieldsGrid>

          <S.Divider />

          <S.SectionTitle>
            {translate('financial.bankData.pixTitle')}
          </S.SectionTitle>

          <S.FieldsGrid>
            <S.Field>
              <span className="field-label">
                {translate('financial.bankData.pixKeyType')}
              </span>
              <span className="field-value">
                {getPixKeyTypeLabel(account.pixKeyType)}
              </span>
            </S.Field>

            <S.Field>
              <span className="field-label">
                {translate('financial.bankData.pixKey')}
              </span>
              <span className="field-value">{account.pixKey}</span>
            </S.Field>
          </S.FieldsGrid>
        </>
      )}

      {!loading && !isEditing && !account && loadError && (
        <S.EmptyState>{translate('financial.bankData.empty')}</S.EmptyState>
      )}

      {!loading && !isEditing && !account && !loadError && (
        <S.EmptyState>{translate('financial.bankData.noData')}</S.EmptyState>
      )}
    </S.Section>
  )
}

export default BankData
